import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import CampoEditavel from "./CampoEditavel";
import ListaEditavel from "./ListaEditavel";
import palette from "../../common/styles/palette";
import ThemesService from "../../services/ThemesService";
import CompetenceService from "../../services/CompetenceService";
import { complex, envir } from "../../scenarios/const-values";
import {
  InterviewDraft,
  InterviewOp,
  InterviewScenario,
} from "../../types/Interview";

/**
 * A aba Cenário — onde o Cenário deixa de ser dado coletado e enterrado.
 *
 * O DEFEITO QUE ELA CORRIGE. A conversa já perguntava o objetivo da Cena, o
 * Roteiro do paciente e a Instrução de avaliação, e o Rascunho já guardava
 * tudo. Só que a tela mostrava cinco chips no cabeçalho e mais nada: o
 * professor respondia e o texto sumia. Quem quisesse revisar o que tinha dito
 * precisava rolar o fio da conversa para trás e ler as próprias falas. É o
 * mesmo defeito que `06-feature-revertida.md` §7.3 item 6 catalogou do lado do
 * banco, agora do lado da tela.
 *
 * O Roteiro do paciente (`patient_disclosure`) é o caso extremo: é a única
 * coluna que SÓ a Entrevista escreve, e até esta aba nenhuma tela do Hiplade a
 * exibia — nem a da Entrevista, nem o formulário manual do Cenário.
 *
 * TODA EDIÇÃO É UM TURNO. Não há salvar, não há formulário, não há segundo
 * caminho de escrita: cada campo emite o mesmo `set` da lista branca por onde
 * o modelo passa, e a Lacuna recomputa no mesmo instante. Escrever a Instrução
 * de avaliação aqui apaga a Lacuna C05 sem que ninguém precise digitar no chat.
 */

/** Enunciado curto, para a frase que entra na conversa não virar parede. */
const curto = (texto: string) =>
  texto.length > 60 ? texto.slice(0, 60) + "…" : texto;

/**
 * O valor é o enum do servidor (`DraftPatch::SCALAR_PATHS['scenario.actor']`);
 * o rótulo é i18n. Coincidem hoje em português, e é justamente por isso que
 * ficam separados: no dia em que a tela for traduzida, o que vai para a coluna
 * não pode mudar junto.
 */
const ATORES: [string, string][] = [
  ["Pessoa Real", "interview_actor_person"],
  ["Manequim", "interview_actor_manikin"],
];

/**
 * Os nove campos de história clínica. Preenchidos em menos de um terço dos
 * Cenários, por isso vivem num bloco recolhido: aberto por padrão, cada um com
 * seu rótulo, eles ocupariam mais tela do que a Cena e o Ator somados e
 * empurrariam para baixo o que quase todo mundo precisa ver.
 */
type ChaveClinica =
  | "illness"
  | "allergy"
  | "habits"
  | "surgeries"
  | "family_health_history"
  | "continuous_use_medications"
  | "temporary_medications"
  | "health_guidelines"
  | "physical_exam";

const CLINICOS: [ChaveClinica, string][] = [
  ["illness", "interview_clinic_illness"],
  ["allergy", "interview_clinic_allergy"],
  ["habits", "interview_clinic_habits"],
  ["surgeries", "interview_clinic_surgeries"],
  ["family_health_history", "interview_clinic_family"],
  ["continuous_use_medications", "interview_clinic_meds_continuous"],
  ["temporary_medications", "interview_clinic_meds_temporary"],
  ["health_guidelines", "interview_clinic_guidelines"],
  ["physical_exam", "interview_clinic_physical_exam"],
];

/**
 * Quantos dos campos PRINCIPAIS ainda estão vazios — o número do badge da aba.
 *
 * A história clínica fica de fora de propósito: contá-la faria o badge nascer
 * em 9 em toda Entrevista nova e virar ruído permanente. O badge só vale
 * enquanto o professor acreditar que ele aponta trabalho de verdade.
 */
export const camposVaziosDoCenario = (draft: InterviewDraft): number => {
  const c = draft.scenario;
  const textos = [
    draft.name,
    c.goal_scene,
    c.description_scene,
    c.scenario_evaluated,
    c.actor,
    c.actor_instructions,
    draft.instructions,
  ];
  const metadadosPresentes = [
    draft.theme_id !== null,
    draft.competence_id !== null,
    ["B", "M", "A"].includes(c.complexity ?? ""),
    [1, 2, 3, 4, 5, 6].includes(Number(c.environment)),
    ["M", "F", "O"].includes(c.sex ?? ""),
    Number(c.age) > 0,
    typeof c.time_duration === "string" &&
      /^\d{2}:\d{2}:\d{2}$/.test(c.time_duration),
  ];

  return (
    textos.filter((v) => !v || v.trim() === "").length +
    metadadosPresentes.filter((presente) => !presente).length +
    ((c.patient_disclosure?.spontaneous ?? []).length === 0 ? 1 : 0) +
    ((c.materials ?? []).length === 0 ? 1 : 0)
  );
};

/**
 * Cabeçalho de seção. Fica no módulo, não dentro do componente: declarar um
 * componente durante o render o remonta a cada tecla, e os campos aqui dentro
 * guardam o texto em digitação — perderíamos o que o professor está escrevendo.
 */
function Secao({
  id,
  titulo,
  ajuda,
  children,
}: {
  id?: string;
  titulo: string;
  ajuda?: string;
  children: React.ReactNode;
}) {
  return (
    <Box id={id} sx={{ mb: 3 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 700,
          letterSpacing: 0.6,
          color: palette.brandDark,
          borderBottom: `2px solid ${palette.brandSoft}`,
          pb: 0.5,
          mb: 1,
        }}
      >
        {titulo.toUpperCase()}
      </Typography>
      {ajuda && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: palette.textMuted, mb: 1 }}
        >
          {ajuda}
        </Typography>
      )}
      {children}
    </Box>
  );
}

type Opcao = { value: string | number; label: string };

const opcoesDaResposta = (response: unknown): Opcao[] => {
  if (!response || typeof response !== "object" || !("data" in response))
    return [];
  const data = response.data;
  if (!Array.isArray(data)) return [];

  const opcoes: Opcao[] = [];
  data.forEach((item: unknown) => {
    if (
      !item ||
      typeof item !== "object" ||
      !("id" in item) ||
      !("name" in item)
    )
      return;
    if (typeof item.id !== "number" || typeof item.name !== "string") return;
    opcoes.push({ value: item.id, label: item.name });
  });
  return opcoes;
};

interface Props {
  campoEmFoco?: string;
  pedidoCampo?: number;
  draft: InterviewDraft;
  busy: boolean;
  /** O mesmo `editar` das outras abas: frase para a conversa + ops da lista branca. */
  onEditar: (mensagem: string, ops: InterviewOp[]) => void;
}

export default function AbaCenario({ draft, busy, onEditar, campoEmFoco, pedidoCampo }: Props) {
  const { t } = useTranslation();
  const [clinicoAberto, setClinicoAberto] = useState(false);
  useEffect(() => {
    if (campoEmFoco === "scenario" || CLINICOS.some(([chave]) => campoEmFoco === `scenario.${chave}`)) {
      setClinicoAberto(true);
    }
  }, [campoEmFoco, pedidoCampo]);
  const [themes, setThemes] = useState<Opcao[]>([]);
  const [competences, setCompetences] = useState<Opcao[]>([]);

  useEffect(() => {
    let ativo = true;

    ThemesService.getAll(1, 0)
      .then((response: unknown) => {
        if (ativo) setThemes(opcoesDaResposta(response));
      })
      .catch(() => undefined);
    CompetenceService.getAll(1, 0)
      .then((response: unknown) => {
        if (ativo) setCompetences(opcoesDaResposta(response));
      })
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, [draft.theme_id, draft.competence_id]);

  const c: InterviewScenario = draft.scenario;
  const roteiro = c.patient_disclosure ?? { spontaneous: [], on_demand: [] };

  const definir = (
    path: string,
    rotulo: string,
    valor: string | number | string[],
    valorVisivel?: string
  ) =>
    onEditar(
      Array.isArray(valor)
        ? `Ajustei ${rotulo} do Cenário: ${valor.length} linha(s).`
        : String(valor).trim() === ""
        ? `Apaguei ${rotulo} do Cenário.`
        : `Escrevi ${rotulo} do Cenário: **${curto(
            valorVisivel ?? String(valor)
          )}**.`,
      [{ op: "set", path, value: valor }]
    );

  /** Campo de texto padrão da aba. `chave` é o rótulo i18n; `path`, a lista branca. */
  const campo = (
    path: string,
    chave: string,
    valor: string,
    multiline = true,
    ajuda?: string
  ) => (
    <Box id={`interview-field-${path}`} sx={{ mb: 1.5 }}>
      <CampoEditavel
        valor={valor}
        label={t(chave) ?? undefined}
        multiline={multiline}
        fullWidth
        disabled={busy}
        onComitar={(v) => definir(path, t(chave), v)}
      />
      {ajuda && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: palette.textMuted, mt: 0.25 }}
        >
          {ajuda}
        </Typography>
      )}
    </Box>
  );

  const selecao = (
    path: string,
    rotulo: string,
    valor: string | number | null,
    opcoes: Opcao[],
    numerico = false
  ) => (
    <TextField
      id={`interview-field-${path}`}
      select
      size="small"
      fullWidth
      disabled={busy}
      label={rotulo}
      value={valor ?? ""}
      onChange={(event) => {
        const bruto = event.target.value;
        const novoValor = numerico ? Number(bruto) : bruto;
        const opcao = opcoes.find(
          (item) => String(item.value) === String(bruto)
        );
        definir(path, rotulo, novoValor, opcao?.label);
      }}
    >
      {valor !== null && !opcoes.some((opcao) => opcao.value === valor) && (
        <MenuItem value={valor}>{`#${valor}`}</MenuItem>
      )}
      {opcoes.map((opcao) => (
        <MenuItem key={opcao.value} value={opcao.value}>
          {opcao.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
      <Secao
        titulo={t("interview_premises_section")}
        ajuda={t("interview_premises_help") ?? undefined}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {selecao(
            "theme_id",
            t("input_label_theme_ch"),
            draft.theme_id,
            themes,
            true
          )}
          {selecao(
            "competence_id",
            t("input_label_competencia_ch"),
            draft.competence_id,
            competences,
            true
          )}
          {selecao(
            "scenario.complexity",
            t("input_label_complexity"),
            c.complexity,
            complex
          )}
          {selecao(
            "scenario.environment",
            t("input_label_environment"),
            c.environment,
            envir,
            true
          )}
          {selecao("scenario.sex", t("input_label_gender"), c.sex, [
            { value: "M", label: t("title_dialog_male") },
            { value: "F", label: t("title_dialog_female") },
            { value: "O", label: t("title_dialog_others") },
          ])}
          <CampoEditavel
            id="interview-field-scenario.age"
            valor={c.age === null ? "" : String(c.age)}
            label={t("input_label_age") ?? undefined}
            numero
            fullWidth
            disabled={busy}
            onComitar={(valor) =>
              definir("scenario.age", t("input_label_age"), Number(valor))
            }
          />
          <CampoEditavel
            id="interview-field-scenario.time_duration"
            valor={(c.time_duration ?? "").slice(0, 5)}
            label={t("input_label_time_duration") ?? undefined}
            fullWidth
            disabled={busy}
            onComitar={(valor) =>
              definir(
                "scenario.time_duration",
                t("input_label_time_duration"),
                /^\d{2}:\d{2}$/.test(valor) ? `${valor}:00` : valor
              )
            }
          />
        </Box>
      </Secao>

      <Secao
        titulo={t("interview_scene_section")}
        ajuda={t("interview_scene_help") ?? undefined}
      >
        {campo("name", "interview_scene_name", draft.name, false)}
        {campo("scenario.goal_scene", "interview_scene_goal", c.goal_scene)}
        {campo(
          "scenario.description_scene",
          "interview_scene_description",
          c.description_scene
        )}
        {campo(
          "scenario.scenario_evaluated",
          "interview_scene_evaluated",
          c.scenario_evaluated ?? "",
          true,
          t("interview_scene_evaluated_help") ?? undefined
        )}
      </Secao>

      <Secao
        titulo={t("interview_actor_section")}
        ajuda={t("interview_actor_help") ?? undefined}
      >
        {/* Enum de dois valores: chip é mais honesto que um select, porque
            mostra as duas opções sem exigir um clique para descobri-las. */}
        <Box id="interview-field-scenario.actor" sx={{ display: "flex", gap: 0.75, mb: 1.5, flexWrap: "wrap" }}>
          {ATORES.map(([valor, chave]) => (
            <Chip
              key={valor}
              size="small"
              label={t(chave)}
              disabled={busy}
              variant={c.actor === valor ? "filled" : "outlined"}
              color={c.actor === valor ? "primary" : "default"}
              onClick={() =>
                definir("scenario.actor", t("interview_actor_who"), valor)
              }
              sx={{ cursor: busy ? "default" : "pointer" }}
            />
          ))}
        </Box>
        {campo(
          "scenario.actor_instructions",
          "interview_actor_instructions",
          c.actor_instructions ?? ""
        )}
      </Secao>

      <Secao
        id="interview-field-scenario.patient_disclosure"
        titulo={t("interview_disclosure_section")}
        ajuda={t("interview_disclosure_help") ?? undefined}
      >
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 600, mt: 1 }}
        >
          {t("interview_disclosure_spontaneous")}
        </Typography>
        <ListaEditavel
          itens={roteiro.spontaneous ?? []}
          disabled={busy}
          vazio={t("interview_disclosure_spontaneous_empty") ?? undefined}
          placeholder={t("interview_disclosure_spontaneous_hint") ?? undefined}
          onComitar={(novos) =>
            definir(
              "scenario.patient_disclosure.spontaneous",
              t("interview_disclosure_spontaneous"),
              novos
            )
          }
        />

        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 600, mt: 2 }}
        >
          {t("interview_disclosure_on_demand")}
        </Typography>
        <ListaEditavel
          itens={roteiro.on_demand ?? []}
          disabled={busy}
          vazio={t("interview_disclosure_on_demand_empty") ?? undefined}
          placeholder={t("interview_disclosure_on_demand_hint") ?? undefined}
          onComitar={(novos) =>
            definir(
              "scenario.patient_disclosure.on_demand",
              t("interview_disclosure_on_demand"),
              novos
            )
          }
        />
      </Secao>

      <Secao
        id="interview-field-scenario.materials"
        titulo={t("interview_materials_section")}
        ajuda={t("interview_materials_help") ?? undefined}
      >
        <ListaEditavel
          itens={c.materials ?? []}
          disabled={busy}
          vazio={t("interview_materials_empty") ?? undefined}
          placeholder={t("interview_materials_hint") ?? undefined}
          onComitar={(novos) =>
            definir(
              "scenario.materials",
              t("interview_materials_section"),
              novos
            )
          }
        />
      </Secao>

      <Secao id="interview-field-scenario" titulo={t("interview_clinic_section")}>
        <Button
          size="small"
          onClick={() => setClinicoAberto((v) => !v)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: clinicoAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          }
        >
          {clinicoAberto
            ? t("interview_clinic_hide")
            : t("interview_clinic_show")}
        </Button>
        <Collapse in={clinicoAberto} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", color: palette.textMuted, mb: 1 }}
            >
              {t("interview_clinic_help")}
            </Typography>
            {CLINICOS.map(([chave, rotulo]) => (
              <React.Fragment key={chave}>
                {campo(`scenario.${chave}`, rotulo, c[chave] ?? "")}
              </React.Fragment>
            ))}
          </Box>
        </Collapse>
      </Secao>

      {/* `instructions` mora na RAIZ do Rascunho, não em `scenario` — por isso
          o caminho não tem prefixo. Foi o campo mais enterrado de todos: a
          conversa perguntava (Lacuna C05), o Rascunho guardava, e a Aplicação
          jogava fora porque a coluna não existia. */}
      <Secao
        titulo={t("interview_instructions_section")}
        ajuda={t("interview_instructions_help") ?? undefined}
      >
        {campo(
          "instructions",
          "interview_instructions_label",
          draft.instructions ?? ""
        )}
      </Secao>
    </Box>
  );
}
